import type { UseSuspenseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { ROADMAP_REMINDER_DAYS } from '../../../config/constants';
import type { RoadmapEvent } from '../../../interfaces/event';
import { isMaturity } from '../../../utils/eventUtils';
import { getSignedAmountForType } from '../../../utils/utils';
import { useClient } from '../../client-api';
import type { AdvisoryNeedTaskDto, EventResponseDto, EventType, LifeEventDto, MaturityDto } from '../../generated/client';
import { getEvents } from '../../generated/client';
import { queryKeys } from '../../query-keys';

export function useEvents(): UseSuspenseQueryResult<EventResponseDto, AxiosError | null> {
    const { data: client } = useClient();
    const queryKey = useMemo(() => queryKeys.events(client?.partnerUuid), [client?.partnerUuid]);

    const mapToEvent = useCallback(
        <TEvent extends MaturityDto | AdvisoryNeedTaskDto | LifeEventDto>(events: TEvent[], type: EventType): RoadmapEvent<TEvent>[] =>
            !events
                ? []
                : events.map((item) => ({
                      ...item,
                      key: `${type}-${isMaturity(item) ? item.qualifiedKey : item.id}`,
                      showReminderNotification: moment(item.reminderDate).diff(moment(), 'days') < ROADMAP_REMINDER_DAYS,
                      //lifeEvents and advisory needs tasks don't have reference amounts.
                      signedReferenceAmount: isMaturity(item)
                          ? getSignedAmountForType(item.referenceAmount, item.type!)
                          : item.amount && item.type
                            ? getSignedAmountForType(item.amount, item.type)
                            : 0,
                      signedAmount: item.signedAmount,
                      referenceCurrency: isMaturity(item) ? item.referenceCurrency : item.currency,
                      isCashFlowRelevant: isMaturity(item) ? true : item.isCashFlowRelevant,
                  })),
        [],
    );

    return useQuery({
        queryKey,
        initialData: { maturities: [], lifeEvents: [], advisoryNeedTasks: [] },
        queryFn: ({ signal }) =>
            getEvents({ path: { clientId: client!.id }, query: { domicile: client!.mainDomicile!.countryCode }, signal }).then((result) => result.data!),
        enabled: !!client,
        select: (events: EventResponseDto) => ({
            maturities: mapToEvent<MaturityDto>(events.maturities, 'MATURITY'),
            lifeEvents: mapToEvent<LifeEventDto>(events.lifeEvents, 'LIFE_EVENT'),
            advisoryNeedTasks: mapToEvent<AdvisoryNeedTaskDto>(events.advisoryNeedTasks, 'ADVISORY_NEED_TASK'),
        }),
    });
}
